import qc from 'qc'
import * as editorScript from '@ps/bridge/qc/scripts/editor'
import { EditorDataScript } from '@ps/bridge/qc/scripts/editor'
import { IvAssetProperty, IvNodeUuid, IvSegmentBase, IvTrack } from './types'
import nodePool from '@ps/bridge/qc/node-pool'
import {
  TweenClass,
  hadAnimation,
  isAudio,
  isDragBound,
  isEmptyNode,
  isImage,
  isParticle,
  isParticleParent,
  isSequenceFrame,
  isText,
  isVideo,
} from '@ps/bridge/qc/base'
import {
  IvAssetType,
  IvPreviewState,
  IvSegmentType,
  IvTrackType,
} from './enums'
import _ from 'lodash'
import {
  getAbsoluteUrl,
  getAbsoluteUrlByNode,
} from '@ps/workbench/undo-redo/module/play-component'
import { getDescendants } from '@ps/bridge/qc/node/utils'
import { scripts } from '@ps/bridge/qc/scripts'
import {
  durationToFrames,
  framesToDuration,
} from '@com/components/timeline-panel/utils/frame'
import store from '@ps/store'
import { IvBrainItem } from '../brain/types'
import { EventBus, Event } from '@ps/utils/event-bus'
import { editAudioAndAddToProject } from '@ps/api/editor/audio'
import router from '@ps/router'
import gameIframe from '@ps/bridge/iframe/iframe'
import {
  getEndHotAreaNode,
  getSceneHotSpotGroup,
  getVideoNameByPath,
} from '../brain/brain'
import { getEventHandByNodeUuid } from '../event/event'
import { pausePsBgm, playPsBgm } from '@ps/store/workbench/common/bgm'
import { getVisibleByLayoutString } from '@ps/bridge/qc/node/layout'
import action from '../brain/action'
import { VpMethods } from '@ps/bridge/qc/vp/config'

/**
 * 根据场景节点uuid获取场景节点的时间轴数据
 * @param sceneUuid 场景节点uuid
 * @returns
 */
export function getTimelineBySceneUuid(sceneUuid: IvNodeUuid): IvTrack[] {
  const sceneNode = nodePool.find(sceneUuid)
  const ped = editorScript.getScript(sceneNode) as EditorDataScript
  if (ped.$data.timeline) {
    return _.cloneDeep(Object.values(ped.$data.timeline))
  }
  return []
}

/**
 * 设置时间轴数据到指定场景节点中持久化
 * @param sceneUuid
 * @param data
 */
export function setTimeline(sceneUuid: IvNodeUuid, data: IvTrack[]) {
  const sceneNode = nodePool.find(sceneUuid)
  const ped = editorScript.getScript(sceneNode) as EditorDataScript
  ped.$data.timeline = _.cloneDeep(data)
}

/**
 * 根据节点uuid获取节点的asset类型信息
 * @param nodeUuid
 * @returns
 */
export function getIvAsset(nodeUuid: IvNodeUuid): IvAssetProperty['asset'] {
  const node = nodePool.find(nodeUuid)
  if (!node || isText(node) || isEmptyNode(node)) {
    return null
  }
  let type
  let url = ''
  let duration
  const name = isVideo(node)
    ? getVideoNameByPath(node.Video.videoUrl)
    : node.name
  if (isImage(node)) {
    type = IvAssetType.Image
    url = getAbsoluteUrlByNode(node)
  } else if (isSequenceFrame(node)) {
    type = IvAssetType.SequenceFrame
    url = getAbsoluteUrlByNode(node)
  } else if (isDragBound(node)) {
    type = IvAssetType.DragonBone
    url = getAbsoluteUrlByNode(node)
  } else if (isParticle(node)) {
    type = IvAssetType.Particle
    url = getAbsoluteUrlByNode(node)
  } else if (isParticleParent(node)) {
    const children = getDescendants(node, false, false)
    const particleNode = children.find((child) => isParticle(child))
    type = IvAssetType.Particle
    url = getAbsoluteUrlByNode(particleNode)
  } else if (isVideo(node)) {
    type = IvAssetType.Video
    const video = node.getScript(scripts.video.name)
    if (/^http[s]?:\/\//.test(video.sourceVideoUrl)) {
      url = video.sourceVideoUrl
    } else {
      url = getAbsoluteUrl(video.sourceVideoUrl)
    }
    duration = video.sourceVideoDuration
  } else if (isAudio(node)) {
    type = IvAssetType.Audio
    const audio = node.getScript(scripts.audio.name)
    if (/^http[s]?:\/\//.test(audio.sourceAudioUrl)) {
      url = audio.sourceAudioUrl
    } else {
      url = getAbsoluteUrl(audio.sourceAudioUrl)
    }
    duration = audio.sourceAudioDuration
  }
  return {
    url,
    type,
    name,
    duration,
  }
}

function parseTimeline(timeline: IvTrack[]): {
  freezes: IvSegmentBase[]
  loops: IvSegmentBase[]
  holds: IvSegmentBase[]
  videoDuration: number
} {
  const interactive = timeline.find((item: IvTrack) => {
    return item.type === IvTrackType.Iv
  })
  let freezes: IvSegmentBase[], loops, holds
  if (interactive) {
    freezes = interactive.segments.filter((item: IvSegmentBase) => {
      return item.type === 'freeze'
    })
    freezes.sort((a, b) => {
      if (a.start < b.start) {
        return -1
      }
      return 1
    })
    loops = interactive.segments.filter((item) => {
      return item.type === 'loop'
    })
    loops.sort((a, b) => {
      if (a.start < b.start) {
        return -1
      }
      return 1
    })
    holds = interactive.segments.filter((item) => {
      return item.type === 'hold'
    })
    holds.sort((a, b) => {
      if (a.start < b.start) {
        return -1
      }
      return 1
    })
  } else {
    freezes = []
    loops = []
    holds = []
  }
  const video = timeline.find((item) => {
    return item.type === IvTrackType.Video
  })
  const videoDuration = video.segments[0].frames
  return { freezes, loops, holds, videoDuration }
}
function cookId(id: string): string {
  return id.slice(0, 8)
}
export function hasAction(sceneNode, nodeUuid, method) {
  const ast = sceneNode.getScript('playsmart.editor.data').vpAst
  for (const i in ast) {
    const resCondition = ast[i].condition.some(
      (condition) => condition.node === nodeUuid,
    )
    const resAction = ast[i].action.some((action) => action.method === method)
    if (resCondition && resAction) return true
  }
  return false
}
function resolveTimelineAwakeCode(
  scriptCode: string,
  timeline: IvTrack[],
  sceneNode: qc.Node,
): string {
  const timelineConstructReg = new RegExp(
    `(// ## timeline awake ##)([\\w\\W]*)(// ## timeline awake ##)`,
  )
  let timelineConstructCode = `
  _this.curTime = 0;
  _this.lastTime = 0;
  var gamePlayPed = this.gameObject.parent.getScript('playsmart.editor.data');
  var brain = gamePlayPed.$data.brain;
  var brainItem = brain.find(function(item) {
    return item.sceneUuid === _this.gameObject.uuid;
  })
  if (!brainItem) return;
  _this.endHotAreaNodeUuids = brainItem.actions.map(function(item) {
    return item.nodeUuid;
  }).filter((function(item) {
    return !!item;
  }))
  _this.linkType = brainItem.link.type;
  if (brainItem.actions.length > 0) {
    _this.jumpSceneId = brainItem.actions[0].targetUuid;
  }
  var ped = this.gameObject.getScript('playsmart.editor.data');
  if (ped.$data.timeline) {
    this.timelineData = Object.values(ped.$data.timeline);
  }
  _this.video1Data = _this.timelineData.find(function(item) {
    return item.type === 'video';
  })
  _this.video1Data = _this.video1Data.segments[0];

  _this.freezes = _this.timelineData.find(function(item) {
    return item.type === 'iv';
  })
  if (_this.freezes) {
    _this.freezes = _this.freezes.segments.filter(function(item){
      return item.type === 'freeze';
    })
  }

  _this.loops = _this.timelineData.find(function(item) {
    return item.type === 'iv';
  })
  if (_this.loops) {
    _this.loops = _this.loops.segments.filter(function(item){
      return item.type === 'loop';
    })
  }

  _this.holds = _this.timelineData.find(function(item) {
    return item.type === 'iv';
  })
  if (_this.holds) {
    _this.holds = _this.holds.segments.filter(function(item){
      return item.type === 'hold';
    })
  }

  _this.dir = 1;
  `
  const { freezes, loops, holds } = parseTimeline(timeline)
  freezes.forEach((freeze) => {
    timelineConstructCode += `_this.freeze_${cookId(freeze.id)} = 0;`
  })
  loops.forEach((loop) => {
    timelineConstructCode += `
    _this.loop_${cookId(loop.id)} = 0;
    _this.loopSubType_${cookId(loop.id)} = '${loop.loopSubType}';
    `
  })
  holds.forEach((hold) => {
    timelineConstructCode += `_this.hold_${cookId(hold.id)} = 0;`
  })

  timelineConstructCode += `
    var ua = navigator.userAgent;
    _this.isIos = /iPhone|iPad|iPod/i.test(ua) && /OS \\d+_\\d+(_\\d+)?/i.test(ua);
    _this.video1 = qc_game.nodePool.find(_this.video1Data.id);
    ps.mainState.add("scenechange", async function(uuid, uniqueName){
      var sceneNode = qc_game.nodePool.find(uuid);
      if (!ps.SceneConfig.isNormalScene(sceneNode)) return;
      if(!0&&_this.gameObject.uuid === uuid) {
        _this.lastTime = new Date().getTime();
        _this.curTime = 0;
        if (ps.Video.__ability.split(',').indexOf('canvas') === -1) {
          if (!_this.canvas) {
            _this.canvas = document.createElement('canvas');
            _this.video1.div.appendChild(_this.canvas);
            _this.ctx = _this.canvas.getContext('2d',{ alpha: false, willReadFrequently: true });
            _this.imageDatas = [];
            _this.tick = 0;
          }
        }
        _this.timelineRender();
      } else {
        if (ps.Video.__ability.split(',').indexOf('canvas') === -1) {
          _this.video1.Video.pause();
          if (_this.canvas) {
            _this.imageDatas = [];
            _this.canvas.remove();
            _this.canvas = null;
          }
        }
      }
    })
  `
  freezes.forEach((freeze) => {
    const eventHand = getEventHandByNodeUuid(sceneNode, freeze.id)
    if (!freeze.isEnd) {
      timelineConstructCode += `
      ps.mainState.add("${eventHand}", async function(uuid, uniqueName) {
        if(!0&&'${freeze.id}' === uuid) {
          if (_this.freeze_${cookId(freeze.id)} === 1) {
            _this.freeze_${cookId(freeze.id)} = 2;
            _this.video1.Video.player.currentTime = ${
              framesToDuration(freeze.start) / 1000
            };
            _this.video1.Video.play(false);
          }
        }
      }, this)
      `
    }
  })

  holds.forEach((hold) => {
    if (!hold.isEnd) {
      timelineConstructCode += `
      ps.mainState.add("DOWN", async function(uuid, uniqueName) {
        if(!0&&'${hold.id}' === uuid) {
          if (_this.hold_${cookId(hold.id)} === 1) {
            _this.hold_${cookId(hold.id)} = 2;
            _this.video1.Video.play(false);
          }
        }
      }, this)

      ps.mainState.add("UP", async function(uuid, uniqueName){
        if(!0&&'${hold.id}' === uuid) {
          if (_this.hold_${cookId(hold.id)} === 2) {
            _this.hold_${cookId(hold.id)} = 1;
            _this.video1.Video.pause();
          }
        }
      }, this)
      `
    }
  })

  loops.forEach((loop) => {
    const eventHand = getEventHandByNodeUuid(sceneNode, loop.id)
    const loopEnd = framesToDuration(loop.start + loop.frames)
    if (!loop.isEnd) {
      timelineConstructCode += `
        ps.mainState.add("${eventHand}", async function(uuid, uniqueName) {
          if(!0&&'${loop.id}' === uuid) {
            if (_this.loop_${cookId(loop.id)} === 1) {
              _this.dir = 1;
              _this.loop_${cookId(loop.id)} = 2;
              _this.curTime = ${loopEnd};
              _this.video1.Video.player.currentTime = ${loopEnd / 1000};
              _this.video1.Video.player.play();
              if (_this.video1.Video.audioUrl && !_this.video1.Video.isMute) {
                ps.Audio.pauseSound(_this.video1.Video.audioName, 'game');
                ps.Audio.playSoundAtTime(_this.video1.Video.audioName, ${
                  loopEnd / 1000
                });
              }
              _this.isInLoop = false;
              _this.isInDoubleLoop = false;
              if (ps.Video.__ability.split(',').indexOf('canvas') > -1) {
                _this.video1.Video.clearImageDatas();
              } else {
                _this.doubleLoopFramesDraw = 1;
                _this.imageDatas = [];
                _this.tick = 0;
              }
            }
          }
        }, this)
      `
    }
  })
  scriptCode = scriptCode.replace(
    timelineConstructReg,
    `$1$2 ${timelineConstructCode} $3`,
  )
  return scriptCode
}

function resolveTimelineUpdateCode(
  scriptCode: string,
  timeline: IvTrack[],
  isLastScene: boolean,
): string {
  const timelineUpdateReg = new RegExp(
    `(// ## timeline update ##)([\\w\\W]*)(// ## timeline update ##)`,
  )
  const { freezes, loops, holds, videoDuration } = parseTimeline(timeline)
  let timelineUpdateCode = ``

  timelineUpdateCode += `
    if (this.ending && !this.isInLoop) return;
    if (!this.video1.Video.player) return;
    var t = new Date().getTime();
    var del = (t - this.lastTime);
    this.lastTime = t;
    if (!this.isInLoop && this.jumpSceneId && this.linkType === 'auto' && Math.abs(_this.video1.Video.player.currentTime * 1000 - ${framesToDuration(
      videoDuration,
    )}) <= 67 ) {
      // 自动跳转,有时发布渠道包后，视频播放完了，currentTime会有误差，比实际的duration小2帧，这里加一个误差范围兼容
      this.gameObject.vpSwitchScene({ jumpSceneId: _this.jumpSceneId });
    }
    this.timelineRender();
    // console.log(this.curTime);
  `

  freezes.forEach((freeze) => {
    timelineUpdateCode += `if(this.freeze_${cookId(freeze.id)} === 1) return;\n`
  })

  holds.forEach((hold) => {
    timelineUpdateCode += `if(this.hold_${cookId(hold.id)} === 1) return;\n`
  })
  timelineUpdateCode += `
  if (this.curTime === ${framesToDuration(videoDuration)} && this.dir === -1) {
    // 场景间双向的互动循环到结尾时需要提前设置curTime
    this.curTime += del * this.dir;
  }`
  if (isLastScene) {
    timelineUpdateCode += `if(this.curTime >= ${framesToDuration(
      videoDuration,
    )} && !this.ending) {
      ps.gameEnd();
      if (this.gameObject.parent.getScript('playsmart.editor.data').$data.autoInstallWhenGameEnd) {
        ps.install();
      }
      this.ending = true;
    }
    `
  }
  timelineUpdateCode += `if(this.curTime >= ${framesToDuration(
    videoDuration,
  )}) return;\n`
  timelineUpdateCode += `
    if (_this.video1.Video.isPlaying) {
      this.curTime = _this.video1.Video.player.currentTime * 1000;
    } else {
      this.curTime += del * this.dir;
    }
  `

  freezes.forEach((freeze) => {
    if (freeze.isEnd) {
      timelineUpdateCode += `
        if (_this.curTime >= ${framesToDuration(
          freeze.start,
        )} && _this.freeze_${cookId(freeze.id)} === 0 && !_this.isInLoop) {
          _this.freezeEnding = true;
        }
      `
    } else {
      timelineUpdateCode += `
        if (_this.curTime >= ${framesToDuration(
          freeze.start,
        )} && _this.freeze_${cookId(freeze.id)} === 0 && !_this.isInLoop) {
          _this.curTime = ${framesToDuration(freeze.start)};
          _this.freeze_${cookId(freeze.id)} = 1;
          _this.video1.Video.player.currentTime = ${
            framesToDuration(freeze.start) / 1000
          };
          _this.video1.Video.pause();
          _this.freezeEnding = true;
        }
      `
    }
  })
  holds.forEach((hold) => {
    timelineUpdateCode += `
      if (_this.curTime >= ${framesToDuration(
        hold.start,
      )} && _this.hold_${cookId(hold.id)} === 0 && !_this.isInLoop) {
        _this.curTime = ${framesToDuration(hold.start)};
        _this.hold_${cookId(hold.id)} = 1;
        _this.video1.Video.player.currentTime = ${
          framesToDuration(hold.start) / 1000
        };
        _this.video1.Video.pause();
      }
      if (_this.curTime > ${framesToDuration(
        hold.start + hold.frames,
      )} && _this.hold_${cookId(hold.id)} === 2) {
        _this.hold_${cookId(hold.id)} = 3;
      }
    `
  })

  loops.forEach((loop) => {
    timelineUpdateCode += `
      if (_this.curTime >= ${framesToDuration(
        loop.start,
      )} && _this.loop_${cookId(loop.id)} === 0) {
        _this.loop_${cookId(loop.id)} = 1;
        _this.isInLoop = true;
        if (_this.loopSubType_${cookId(
          loop.id,
        )} === 'double' && _this.video1.Video.audioUrl) {
          ps.Audio.pauseSound(_this.video1.Video.audioName, 'game');
        }
        if (_this.loopSubType_${cookId(loop.id)} === 'double') {
          _this.isInDoubleLoop = true;
          if (ps.Video.__ability.split(',').indexOf('canvas') > -1) {
            _this.video1.Video.updateDrawFrameCircle(${loop.frames});
          } else {
            _this.doubleLoopFrames = ${loop.frames};
            if (_this.isIos) {
              _this.doubleLoopFramesDraw = Math.ceil(_this.doubleLoopFrames / 150);
              if (_this.doubleLoopFramesDraw === 0) {
                _this.doubleLoopFramesDraw = 1;
              }
            } else {
              _this.doubleLoopFramesDraw = 1;
            }
          }
        }
      }
      if (_this.loop_${cookId(
        loop.id,
      )} === 1 && _this.curTime >= ${framesToDuration(
      loop.start + loop.frames,
    )} && _this.loopSubType_${cookId(loop.id)} === 'single') {
        _this.curTime = ${framesToDuration(loop.start)};
        _this.video1.Video.player.currentTime = ${
          framesToDuration(loop.start) / 1000
        };
        // ios场景间的单向循环播放到最后会被pause，这里重新play，确保不被暂停。
        _this.video1.Video.player.play();
        if (_this.video1.Video.audioUrl && !_this.video1.Video.isMute) {
          ps.Audio.pauseSound(_this.video1.Video.audioName, 'game');
          ps.Audio.playSoundAtTime(_this.video1.Video.audioName, ${
            framesToDuration(loop.start) / 1000
          });
        }
      }
      if (_this.loop_${cookId(
        loop.id,
      )} === 1 && _this.curTime >= ${framesToDuration(
      loop.start + loop.frames,
    )} && _this.loopSubType_${cookId(loop.id)} === 'double') {
        _this.curTime = ${framesToDuration(loop.start + loop.frames)};
        _this.dir = -1;
        _this.video1.Video.pause();
      } else if (_this.loop_${cookId(
        loop.id,
      )} === 1 && _this.curTime <= ${framesToDuration(
      loop.start,
    )} && _this.loopSubType_${cookId(loop.id)} === 'double') {
        _this.curTime = ${framesToDuration(loop.start)};
        _this.dir = 1;
      }
    `
  })
  scriptCode = scriptCode.replace(
    timelineUpdateReg,
    `$1$2 ${timelineUpdateCode} $3`,
  )
  return scriptCode
}
function resolveTimelineRenderCode(
  scriptCode: string,
  timeline: IvTrack[],
): string {
  const timelineRenderReg = new RegExp(
    `(// ## timeline render ##)([\\w\\W]*)(// ## timeline render ##)`,
  )
  const { freezes, loops, holds, videoDuration } = parseTimeline(timeline)
  let timelineRenderCode = ``
  timelineRenderCode += `
    if (ps.Video.__ability.split(',').indexOf('canvas') > -1) {
      if (_this.isInDoubleLoop) {
        _this.video1.Video.addImageData(_this.curTime);
      }
      if (_this.isInDoubleLoop) {
        _this.video1.Video.renderRealVideoCanvasByImageDatas(_this.curTime);
      } else {
        _this.video1.Video.renderRealVideoCanvas();
      }
    } else {
      if(!_this.canvas) return;
      _this.canvas.width = _this.video1.div.offsetWidth;
      _this.canvas.height = _this.video1.div.offsetHeight;
      _this.video1.Video.player.style.visibility = 'hidden';
      _this.canvas.style.visibility = 'visible';

      if(_this.isInDoubleLoop && (_this.imageDatas.length === 0 || _this.curTime > _this.imageDatas[_this.imageDatas.length - 1].time)) {
        if (_this.tick % _this.doubleLoopFramesDraw === 0) {
          _this.ctx.drawImage(_this.video1.Video.player, 0, 0, _this.video1.Video.videoCanvas.videoWidth, _this.video1.Video.videoCanvas.videoHeight, 0, 0, _this.canvas.width, _this.canvas.height);
          var data = _this.ctx.getImageData(0, 0, _this.canvas.width, _this.canvas.height);
          _this.imageDatas.push({
            time: _this.curTime,
            data: data,
          })
        }
        _this.tick++
      }
      if (_this.isInDoubleLoop) {
        var index = _this.imageDatas.length - 1;
        if (_this.curTime < _this.imageDatas[0].time) {
          index = 0;
        } else if (_this.curTime >= _this.imageDatas[_this.imageDatas.length - 1].time) {
          index = _this.imageDatas.length - 1;
        } else {
          for (var i = 0; i < _this.imageDatas.length; i++) {
            if (_this.curTime >= _this.imageDatas[i].time && _this.curTime < _this.imageDatas[i + 1].time) {
              index = i;
              break;
            }
          }
        }
        // console.log(_this.curTime, index, _this.imageDatas.length);
        _this.canvas.width = _this.imageDatas[index].data.width;
        _this.canvas.height = _this.imageDatas[index].data.height;
        var scaleX = _this.video1.div.offsetWidth / _this.imageDatas[index].data.width;
        var scaleY = _this.video1.div.offsetHeight / _this.imageDatas[index].data.height;
        _this.canvas.style.transform = 'scale('+scaleX+','+scaleY+')';
        _this.canvas.style.transformOrigin = 'left top';
        _this.ctx.putImageData(_this.imageDatas[index].data, 0, 0);
      } else {
        _this.canvas.width = _this.video1.div.offsetWidth;
        _this.canvas.height = _this.video1.div.offsetHeight;
        _this.canvas.style.transform = 'scale(1,1)';
        _this.ctx.drawImage(_this.video1.Video.player, 0, 0, _this.video1.Video.videoCanvas.videoWidth, _this.video1.Video.videoCanvas.videoHeight, 0, 0, _this.canvas.width, _this.canvas.height);
      }
    }
    if (_this.curTime >= ${framesToDuration(videoDuration)}) return;
    // 兜底处理幽灵热区节点，先全部隐藏热区节点，再根据时间轴数据处理显隐
    var ivHotAreaGroupNode = this.gameObject.getChild('iv_hotArea_group');
    if (ivHotAreaGroupNode) {
      ivHotAreaGroupNode.children.forEach(function(child){
        child.visible = false;
      })
    }
    this.timelineData.forEach(function(timeline) {
      timeline.segments.forEach(function(segment) {
        var nodes = [];
        if (segment.isEnd) {
          nodes = _this.endHotAreaNodeUuids;
        } else {
          nodes.push(segment.id);
        }
        nodes = nodes.map(function(uuid){
          return qc_game.nodePool.find(uuid);
        });
        var start, end
        if (segment.type === 'freeze' && !segment.isEnd) {
          // 互动定格的frames默认为15，用于渲染，这里需要特殊处理逻辑
          start = _this.framesToDuration(segment.start)
          end = _this.framesToDuration(segment.start + 1)
        } else {
          start = _this.framesToDuration(segment.start)
          end = _this.framesToDuration(segment.start + segment.frames)
        }
        nodes.forEach(function(node) {
          if (## freeze hold ##) {
            _this.video1.Video.pause();
          } else {
            if (!_this.video1.Video.isPlaying && !_this.isInLoop && !_this.freezeEnding) {
              _this.video1.Video.play(false);
            }
          }
          if (!node) return;
          const Layout = node.getScript('ps.Layout');
          if (Layout) {
            var res = 1;
            if (ps.ScrFix.isP) {
              res = _this.getVisibleByLayoutString(Layout.layout.pdef);
            } else {
              res = _this.getVisibleByLayoutString(Layout.layout.ldef);
            }
            if (res === 0) {
              // layout里记录了visible为0就直接隐藏，不用走下面的逻辑了
              node.visible = false;
              return;
            }
          }
          if (_this.curTime >= start && _this.curTime < end) {
            node.visible = true;
            if (!node.isPlaying) {
              // 播放动画
              if (node.getScript('qc.Tween')) {
                node.getScript('qc.Tween').vpPlayTween()
                node.isPlaying = true
              }
              // 播放音效
              if (node.getScript('ps.AudioNode')) {
                const audio = node.getScript('ps.AudioNode')
                const audioName = audio.getAudioName()
                const audioStage = audio.getAudioStage()
                ps.Audio.playSound(
                  audioName,
                  audioStage,
                  'mp3',
                  audio.loopNumber,
                  () => {},
                  node.uuid,
                )
                node.isPlaying = true
              }
              // 播放粒子,vpPlayParticle里面会判断是否是粒子
              node.vpPlayParticle()
              node.isPlaying = true
            }
          } else {
            node.visible = false;
          }
        });
      })
    })
  `
  const freezeHoldReg = new RegExp(`## freeze hold ##`)
  let freezeHoldCode = `0`
  freezes.forEach((freeze) => {
    freezeHoldCode += `|| _this.freeze_${cookId(freeze.id)} === 1`
  })
  holds.forEach((hold) => {
    freezeHoldCode += `|| _this.hold_${cookId(hold.id)} === 1`
  })
  timelineRenderCode = timelineRenderCode.replace(freezeHoldReg, freezeHoldCode)
  scriptCode = scriptCode.replace(
    timelineRenderReg,
    `$1$2 ${timelineRenderCode} $3`,
  )
  return scriptCode
}
// 没有结束页的情况，普通场景是否是最后一个场景
export function isLastNormalScene(sceneNode: qc.Node): boolean {
  const brainData = store.state.ivBrain.brainData
  const sceneData = brainData.find(
    (scene: IvBrainItem) => scene.sceneUuid === sceneNode.uuid,
  )
  let lastScene = false
  if (sceneData && sceneData.actions.length === 0) {
    lastScene = true
  }
  const sc = sceneNode.getScript(scripts.sceneConfig.name)
  if (!sc.isEnding && lastScene) {
    return true
  }
  return false
}
export function resolveTimeline(
  scriptCode: string,
  sceneNode: qc.Node,
): string {
  const timeline = getTimelineBySceneUuid(sceneNode.uuid)
  if (timeline.length === 0) return scriptCode
  scriptCode = resolveTimelineAwakeCode(scriptCode, timeline, sceneNode)
  const isLastScene = isLastNormalScene(sceneNode)
  scriptCode = resolveTimelineUpdateCode(scriptCode, timeline, isLastScene)
  scriptCode = resolveTimelineRenderCode(scriptCode, timeline)
  return scriptCode
}
// 视频是否已经真实裁剪过
function hasCutVideo(videoSegment, videoComponent) {
  const trimStart = framesToDuration(videoSegment.trimStart)
  // 和resolveIvSegmentVideo里时长转帧数的逻辑保持一致
  const videoFrames = Math.floor(
    durationToFrames(
      videoComponent.clipEndTime - videoComponent.clipStartTime,
      false,
    ),
  )
  if (
    trimStart !== videoComponent.clipStartTime ||
    videoSegment.frames !== videoFrames
  ) {
    // 未真实裁剪过视频
    return false
  }
  return true
}
// 裁剪所有时间轴中的视频
export async function cutTimelineRealVideo() {
  const gamePlay = store.getters['workspace/game/gamePlay']
  for (const i in gamePlay.children) {
    const scene = gamePlay.children[i]
    const timelineData = getTimelineBySceneUuid(scene.uuid)
    // eslint-disable-next-line no-continue
    if (timelineData.length === 0) continue
    const video = timelineData.find((item) => {
      return item.type === IvTrackType.Video
    })
    // eslint-disable-next-line no-continue
    if (!video) continue
    const uuid = video.segments[0].id
    const node = nodePool.find(uuid)
    // eslint-disable-next-line no-continue
    if (!node) continue
    const videoComponent = node.getScript(scripts.video.name)
    const trimStart = framesToDuration(video.segments[0].trimStart)
    const trimEnd = framesToDuration(
      video.segments[0].trimStart + video.segments[0].frames,
    )
    if (!hasCutVideo(video.segments[0], videoComponent)) {
      await store.dispatch('workspace/game/video/cutVideoToProject', {
        stime: trimStart,
        etime: trimEnd,
        url: videoComponent.sourceVideoUrl,
        isUpdateNode: true,
        nodes: node,
      })
    }
  }
  EventBus.$emit(Event.RESOURCE_POOL_REFRESH)
}
// 裁剪所有时间轴中的音频
export async function cutTimelineRealAudio() {
  const gamePlay = store.getters['workspace/game/gamePlay']
  for (const i in gamePlay.children) {
    const scene = gamePlay.children[i]
    const timelineData = getTimelineBySceneUuid(scene.uuid)
    // eslint-disable-next-line no-continue
    if (timelineData.length === 0) continue
    const audio = timelineData.find((item) => {
      return item.type === IvTrackType.Audio
    })
    // eslint-disable-next-line no-continue
    if (!audio) continue
    for (const j in audio.segments) {
      const uuid = audio.segments[j].id
      const node = nodePool.find(uuid)
      // 数据错乱兜底（时间轴里的音频元件的id是视频节点的uuid）
      if (node && isAudio(node)) {
        const audioComponent = node.getScript(scripts.audio.name)
        const trimStart = framesToDuration(audio.segments[j].trimStart)
        const trimEnd = framesToDuration(
          audio.segments[j].trimStart + audio.segments[j].frames,
        )
        const audioFrames = Math.floor(
          durationToFrames(
            audioComponent.clipEndTime - audioComponent.clipStartTime,
            false,
          ),
        )
        // 已经裁剪过的音频就不用裁剪了
        if (
          trimStart !== audioComponent.clipStartTime ||
          audio.segments[j].frames !== audioFrames
        ) {
          const ret = await editAudioAndAddToProject({
            pid: Number(router.currentRoute.query.id),
            url: audioComponent.sourceAudioUrl,
            vol: audioComponent.volume,
            stime: trimStart,
            etime: trimEnd,
          })
          if (ret.code === 0) {
            const data = ret.data
            const config = {
              audioUrl: data.path,
              size: data.file_size,
              clipStartTime: trimStart,
              clipEndTime: trimEnd,
              node,
            }
            store.commit('workspace/game/audio/setAudioBehConfig', config)
          }
        }
      }
    }
  }
  EventBus.$emit(Event.RESOURCE_POOL_REFRESH)
}
// 合并时间轴的时间指针帧转换到当前场景中所处的时间帧以及当前场景首帧在合并轨道中所处的帧数
function frame2FrameInScene(frame: number): number {
  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  const timelineData = store.state.ivTimeline.timelineData
  const videoTimeline = timelineData.find(
    (item) => item.type === IvTrackType.Video,
  )
  const videoSegment = videoTimeline.segments.find(
    (item) => item.sceneUuid === currentSceneNode.uuid,
  )
  const frameInScene = frame - videoSegment.start
  return { frameInScene, startFrame: videoSegment.start }
}

let curTime = 0
let videoComponent
function refreshVideoTime() {
  // console.log('curTime:' + curTime)
  videoComponent.player.currentTime = curTime / 1000
}
// 时间轴预览
export function previewTimeline(frame: number) {
  const iframeWindow = gameIframe.iframe
  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  const timelineData: IvTrack[] = getTimelineBySceneUuid(currentSceneNode.uuid)
  const { freezes, loops, holds, videoDuration } = parseTimeline(timelineData)
  const frameRes = frame2FrameInScene(frame)
  let frameInScene = frameRes.frameInScene
  const startFrame = frameRes.startFrame
  curTime = framesToDuration(frameInScene)
  let lastTime = new Date().getTime()
  let dir = 1
  // 找到当前时间点往右最近的互动定格
  let freeze = freezes.find(
    (freeze) => framesToDuration(freeze.start) >= curTime,
  )
  if (freeze && freeze.start === frameInScene) {
    // 如果当前时间线处于某个互动定格上，跳过该互动定格继续播放
    frame += 1
    frameInScene += 1
    curTime = framesToDuration(frameInScene)
    freeze = freezes.find((freeze) => framesToDuration(freeze.start) >= curTime)
  }
  const midLoop = loops.find(
    (loop) =>
      curTime >= framesToDuration(loop.start) &&
      curTime <= framesToDuration(loop.start + loop.frames),
  )
  if (midLoop) {
    curTime = framesToDuration(midLoop.start) // 如果切换到【互动循环】中间某个时间点时点预览，则跳回该【互动循环】的起始点开始播放
  }
  // 找到当前时间点往右最近的互动循环
  const loop = loops.find((loop) => framesToDuration(loop.start) >= curTime)
  let loopProgress = 0
  // 视频启动播放
  const video = timelineData.find((item) => {
    return item.type === IvTrackType.Video
  })
  const node = nodePool.find(video.segments[0].id)
  videoComponent = node.getScript(scripts.video.name)
  videoComponent.player.addEventListener('timeupdate', refreshVideoTime)
  videoComponent.player.currentTime = curTime / 1000
  if (videoComponent.audioUrl && !videoComponent.isMute) {
    iframeWindow.ps.Audio.pauseSound(videoComponent.audioName, 'game')
    iframeWindow.ps.Audio.playSoundAtTime(
      videoComponent.audioName,
      curTime / 1000,
    )
  }
  // 初始化时间轴中所有节点的isPlaying值
  timelineData.forEach((timeline: IvTrack) => {
    if (timeline.type === IvTrackType.Iv) return
    timeline.segments.forEach((segment) => {
      const node = nodePool.find(segment.id)
      if (node) {
        node.isPlaying = false
      }
    })
  })
  // 获取当前场景跳转到别的场景的热区节点
  const endHotAreaNodeUuids = getEndHotAreaNode(currentSceneNode.uuid)
  store.commit('ivTimeline/updatePreviewState', IvPreviewState.Playing)
  const playFrames = () => {
    if (store.state.ivTimeline.previewState === IvPreviewState.Pause) return
    const t = new Date().getTime()
    const del = t - lastTime
    lastTime = t
    timelineRender(
      curTime,
      timelineData,
      videoDuration,
      endHotAreaNodeUuids,
      true,
    )
    if (curTime === framesToDuration(videoDuration) && dir === -1) {
      // 场景间双向的互动循环到结尾时需要提前设置curTime
      curTime += del * dir
    }
    if (
      freeze &&
      durationToFrames(curTime) >= freeze.start &&
      loopProgress === 0
    ) {
      curTime = framesToDuration(freeze.start)
      // 互动定格重置了时间需要刷新下元素的显隐
      timelineRender(
        curTime,
        timelineData,
        videoDuration,
        endHotAreaNodeUuids,
        true,
      )
      const allSceneFrame = durationToFrames(curTime) + startFrame
      store.commit('ivTimeline/updateCurrentFrame', allSceneFrame)
      pausePreviewTimeline(false)
      return
    }
    if (
      loopProgress === 0 &&
      (durationToFrames(curTime) >= videoDuration || !videoComponent.isPlaying)
    ) {
      curTime = framesToDuration(videoDuration) // 自动跳转的情况
      const allSceneFrame = videoDuration + startFrame
      store.commit('ivTimeline/updateCurrentFrame', allSceneFrame)
      pausePreviewTimeline()
      return
    }
    // console.log(curTime)
    curTime += del * dir
    if (loop) {
      if (curTime >= framesToDuration(loop.start) && loopProgress === 0) {
        loopProgress = 1
        if (loop.loopSubType === 'double' && videoComponent.audioUrl) {
          iframeWindow.ps.Audio.pauseSound(videoComponent.audioName, 'game')
        }
      }
      if (
        loopProgress === 1 &&
        curTime >= framesToDuration(loop.start + loop.frames) &&
        loop.loopSubType === 'single'
      ) {
        curTime = framesToDuration(loop.start)
        if (videoComponent.audioUrl && !videoComponent.isMute) {
          iframeWindow.ps.Audio.pauseSound(videoComponent.audioName, 'game')
          iframeWindow.ps.Audio.playSoundAtTime(
            videoComponent.audioName,
            curTime / 1000,
          )
        }
      }
      if (
        loopProgress === 1 &&
        curTime >= framesToDuration(loop.start + loop.frames) &&
        loop.loopSubType === 'double'
      ) {
        curTime = framesToDuration(loop.start + loop.frames)
        dir = -1
      } else if (
        loopProgress === 1 &&
        curTime <= framesToDuration(loop.start) &&
        loop.loopSubType === 'double'
      ) {
        curTime = framesToDuration(loop.start)
        dir = 1
      }
    }
    const allSceneFrame = durationToFrames(curTime) + startFrame
    store.commit('ivTimeline/updateCurrentFrame', allSceneFrame)
    requestAnimationFrame(playFrames)
  }
  videoComponent.play(false)
  playFrames()
  playPsBgm()
}
// 时间轴每帧渲染的内容
function timelineRender(
  curTime: number,
  timelineData: IvTrack[],
  videoDuration: number,
  endHotAreaNodeUuids: string[],
  needPlay: boolean,
) {
  const iframeWindow = gameIframe.iframe
  if (curTime >= framesToDuration(videoDuration)) return
  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  if (!currentSceneNode) return
  // 兜底处理幽灵热区节点，先全部隐藏热区节点，再根据时间轴数据处理显隐
  const group = getSceneHotSpotGroup(currentSceneNode)
  if (group) {
    group.children.forEach((child: qc.Node) => {
      child.visible = false
    })
  }
  timelineData.forEach((timeline: IvTrack) => {
    timeline.segments.forEach((segment) => {
      let nodes = []
      if (segment.isEnd) {
        nodes = endHotAreaNodeUuids
      } else {
        nodes.push(segment.id)
      }
      nodes = nodes.map((uuid: string) => nodePool.find(uuid))
      let start, end
      if (segment.type === IvSegmentType.Freeze && !segment.isEnd) {
        // 互动定格的frames默认为15，用于渲染，这里需要特殊处理逻辑
        start = framesToDuration(segment.start)
        end = framesToDuration(segment.start + 1)
      } else {
        start = framesToDuration(segment.start)
        end = framesToDuration(segment.start + segment.frames)
      }
      nodes.forEach((node: qc.Node) => {
        if (!node) return
        const Layout = node.getScript('ps.Layout')
        if (Layout) {
          let res = 1
          if (iframeWindow.ps.ScrFix.isP) {
            res = getVisibleByLayoutString(Layout.layout.pdef)
          } else {
            res = getVisibleByLayoutString(Layout.layout.ldef)
          }
          if (res === 0) {
            // layout里记录了visible为0就直接隐藏，不用走下面的逻辑了
            node.visible = false
            return
          }
        }
        if (curTime >= start && curTime < end) {
          node.visible = true
          if (!node.isPlaying && needPlay) {
            // 播放动画
            if (hadAnimation(node)) {
              node.getScript(TweenClass).vpPlayTween()
              node.isPlaying = true
            }
            // 播放粒子
            if (isParticleParent(node) || isParticle(node)) {
              node.vpPlayParticle()
              node.isPlaying = true
            }
            // 播放音效
            if (isAudio(node)) {
              const audio = node.getScript(scripts.audio.name)
              const audioName = audio.getAudioName()
              const audioStage = audio.getAudioStage()
              gameIframe.iframe.ps.Audio.playSound(
                audioName,
                audioStage,
                'mp3',
                audio.loopNumber,
              )
              node.isPlaying = true
            }
          }
        } else {
          node.visible = false
        }
      })
    })
  })
}
// 切换时间指针预览对应的帧
export function timelineRenderByFrame(frame: number) {
  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  if (!currentSceneNode) return
  const brain = store.state.ivBrain.brainData
  const brainItem = brain.find((item) => {
    return item.sceneUuid === currentSceneNode.uuid
  })
  if (!brainItem) return // 判空容错，新增场景节点撤回时，被删除的场景不需要render
  const timelineData = store.state.ivTimeline.timelineData
  const videoTimeline = timelineData.find(
    (item) => item.type === IvTrackType.Video,
  )
  const videoSegment = videoTimeline.segments.find(
    (item) => item.sceneUuid === currentSceneNode.uuid,
  )
  const curFrameInScene = frame - videoSegment.start
  const curTime = framesToDuration(curFrameInScene)
  const curSceneTimelineData: IvTrack[] = getTimelineBySceneUuid(
    currentSceneNode.uuid,
  )
  const endHotAreaNodeUuids = getEndHotAreaNode(currentSceneNode.uuid)
  // 视频播放
  const node = nodePool.find(videoSegment.id)
  if (!node) return
  const videoComponent = node.getScript(scripts.video.name)
  if (videoComponent.player) {
    if (hasCutVideo(videoSegment, videoComponent)) {
      // 已经真实裁剪过的视频
      videoComponent.player.currentTime = curTime / 1000
    } else {
      // 未真实裁剪过的视频要补上视频元件的trimStart
      const videoFrames = curFrameInScene + videoSegment.trimStart
      const curVideoTime = framesToDuration(videoFrames)
      videoComponent.player.currentTime = curVideoTime / 1000
    }
  }
  timelineRender(
    curTime,
    curSceneTimelineData,
    videoSegment.frames,
    endHotAreaNodeUuids,
    false,
  )
  EventBus.$emit(Event.REFRESH_STAGE)
}
// 暂停时间轴预览
export function pausePreviewTimeline(neeStopAnimation = true) {
  if (
    !videoComponent ||
    store.state.ivTimeline.previewState === IvPreviewState.Pause
  ) {
    return
  }
  store.commit('ivTimeline/updatePreviewState', IvPreviewState.Pause)
  videoComponent.player.removeEventListener('timeupdate', refreshVideoTime)
  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  const timelineData: IvTrack[] = getTimelineBySceneUuid(currentSceneNode.uuid)
  const video = timelineData.find((item) => {
    return item.type === IvTrackType.Video
  })
  videoComponent.pause()
  timelineData.forEach((timeline: IvTrack) => {
    timeline.segments.forEach((segment) => {
      const node = nodePool.find(segment.id)
      if (node && node.isPlaying) {
        if (hadAnimation(node) && neeStopAnimation) {
          // 到互动定格元件停下时不暂停动画，手动暂停才暂停动画
          node.getScript(TweenClass).vpStopTween()
          node.isPlaying = false
        }
        if (isParticleParent(node) || isParticle(node)) {
          node.vpStopParticle()
          node.isPlaying = false
        }
        if (isAudio(node)) {
          const audio = node.getScript(scripts.audio.name)
          const audioName = audio.getAudioName()
          const audioStage = audio.getAudioStage()
          gameIframe.iframe.ps.Audio.pauseSound(audioName, audioStage, 'mp3')
          node.isPlaying = false
        }
      }
    })
  })
  pausePsBgm()
}
// 获取合并轨道的总帧数
export function getSumTimelineFrame(): number {
  const timelineData = store.state.ivTimeline.timelineData
  const videoTimeline = timelineData.find(
    (item) => item.type === IvTrackType.Video,
  )
  if (!videoTimeline) {
    return 0
  }
  let sum = 0
  videoTimeline.segments.forEach((item) => {
    if (item) {
      sum += item.frames
    }
  })
  return sum
}
// 获取当前场景在合并时间轴中的时间起点
export function getIvCurSceneStartFrame(): number {
  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  const timelineData = store.state.ivTimeline.timelineData
  if (!currentSceneNode || timelineData.length === 0) return 0
  const videoTimeline = timelineData.find(
    (item) => item.type === IvTrackType.Video,
  )
  const videoSegment = videoTimeline.segments.find(
    (item) => item.sceneUuid === currentSceneNode.uuid,
  )
  return videoSegment.start
}
// 获取当前场景的视频节点
export function getIvCurSceneVideoNode(): qc.Node | undefined {
  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  if (!currentSceneNode) return undefined
  const nodes = getDescendants(currentSceneNode)
  const node = nodes.find((child: qc.Node) => isVideo(child))
  return node
}
